import { useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
    alpha
} from "@mui/material";
import { SecaoConfiguracao } from "../../../../core/components/SecaoConfiguracao";
import { IMarketplace } from "../../../interfaces/IMarketplace";
import { ComportamentoCard, ComportamentosContainer, NenhumComportamentoContainer, TituloComportamentoContainer } from "./styles";
import { IComportamentoWhatsApp } from "../../../interfaces/IComportamentoWhatsAppMarketplace";
import { TipoComportamentoWhatsApp, TipoComportamentoWhatsAppDescricao } from "../../../enum/TipoComportamentoWhatsappMarketplace";
import { IIntegracaoWhatsApp } from "../../../interfaces/IIntegracaoMarketplace";
import { TipoMarketplace, TipoMarketplaceCor, TipoMarketplaceTitulo } from "../../../enum/TipoMarketplace";
import { useNavigate } from "react-router-dom";
import { useMarketplaces } from "../../../hooks/useMarketplaces";
import { Add, ArrowForward, AutoAwesome, Delete, Edit, SmartToyOutlined } from "@mui/icons-material";
import { useSnackbar } from "../../../../core/contexts/SnackbarContext";
import { IAtualizacaoKDSEstacao, IMensagensCustomizadasComportamentoWhatsApp } from "../../../interfaces/IMensagensCustomizadasComportamentoWhatsApp";
import { useEstacoesKDS } from "../../../../cozinha/hooks/useEstacoesKDS";
import { TipoIAWhatsApp, TipoIAWhatsAppDescricao } from "../../../enum/TipoIAWhatsApp";
import { IComportamentoAtendimentoWhatsApp } from "../../../interfaces/IComportamentoAtendimentoWhatsApp";
import { montarMensagemLojaFechadaPadrao } from "../../../utils/mensagensWhatsApp";
import { IEstacaoKDS } from "../../../../cozinha/interfaces/IKDS";

interface ConfigurarComportamentosWhatsAppProps {
    marketplaces: IMarketplace[];
    marketplace: IMarketplace;
    refresh: () => void;
}

const MENSAGENS_PADRAO = {
    simples: {
        mensagemBoasVindas: "Olá {nomeCliente}! 😊\n\nObrigado por entrar em contato conosco!\n\nVocê pode fazer seu pedido através do nosso cardápio digital:\n{linkCardapio}\n\nQualquer dúvida, estamos à disposição!",
    },
    hibrida: {
        mensagemEscolha: "Olá, {cumprimento} {nomeCliente}! 😊\n\nVocê gostaria de fazer seu pedido sozinho pelo cardápio digital ou prefere atendimento com um de nossos atendentes?\n\n*1* - Fazer o pedido pelo cardápio digital\n*2* - Falar com um atendente",
        mensagemAutoatendimento: "Ótimo! Para fazer o seu pedido, acesse o cardápio digital do *{nomeCardapio}* pelo link abaixo:\n\n{linkCardapio}",
    },
    ia: {
        mensagemLinkCardapio: "Seja bem-vindo ao autoatendimento do *{nomeCardapio}*! Para fazer o seu pedido, acesse o link abaixo:\n\n{linkCardapio}",
    },
    resgate: {
        mensagemResgatePedido: "Olá {nomeCliente}! 👀\n\nNotamos que você ainda não concluiu o seu pedido. É só clicar no link abaixo para finalizar:\n\n{linkCardapio}\n\nAinda dá tempo de garantir o seu! 😋",
    },
    avaliacao: {
        mensagemAvaliacao: "Olá {nomeCliente}! 😊\n\nEsperamos que você tenha gostado do seu pedido!\n\nClique no link abaixo e conte para a gente como foi — leva menos de um minuto:\n\n{linkAvaliacao}",
    },
    atualizacoes: {
        aguardando_confirmacao: "🎉 Seu pedido foi recebido com sucesso e está aguardando confirmação do estabelecimento. Assim que confirmarmos, avisaremos você por aqui! 😉",
        em_preparo: "👨‍🍳 Seu pedido foi recebido e já está em preparo na cozinha! Estamos caprichando em cada detalhe pra ficar do jeitinho que você merece 😋",
        em_transporte: "🚗💨 Seu pedido já saiu para entrega e está a caminho! Fique de olho, em breve ele chega até você 📍",
        concluido: "🙏 Muito obrigado pelo pedido e pela preferência! Esperamos te ver novamente em breve 😊🍽️",
        cancelado: "⚠️ Seu pedido foi cancelado. Se tiver qualquer dúvida ou precisar de ajuda, é só responder esta mensagem que a gente te atende 🙂",
        em_andamento: "⏳ Seu pedido está em andamento! Estamos cuidando de tudo por aqui para que dê tudo certo com a sua experiência 🙌",
        pronto_para_retirada: "📦 Seu pedido está pronto para retirada! Venha buscar quando quiser, estamos te esperando com tudo preparado 😄",
        em_negociacao: "💬 Seu pedido está em negociação com o estabelecimento neste momento. Assim que tivermos uma definição, avisamos você por aqui 👍",
        aguardando_resposta_cliente: "🙋 Estamos aguardando a sua resposta para continuar com o seu pedido. Me conta aqui o que você prefere para a gente seguir com tudo certinho 😉",
    },
};

const filtrarMarketplacesCardapio = (marketplaces: IMarketplace[]) =>
    marketplaces?.filter(marketplace => marketplace.tipo === TipoMarketplace.CARDAPIO_DIGITAL_DELIVERY_RETIRADA) ?? [];

const SelecionarMarketplaceCardapio = ({ marketplaces, valor, onChange }: {
    marketplaces: IMarketplace[],
    valor?: string,
    onChange: (uuid: string) => void
}) => {
    const marketplacesValidos = filtrarMarketplacesCardapio(marketplaces);

    return (
        <FormControl fullWidth sx={{ mt: "1.5rem" }}>
            <InputLabel>Marketplaces</InputLabel>
            <Select
                label={"Marketplaces"}
                fullWidth
                value={valor ?? ""}
                onChange={(e) => onChange(e.target.value as string)}
            >
                {marketplacesValidos.length > 0 ? (
                    marketplacesValidos.map((mp) => (
                        <MenuItem key={mp.uuid} value={mp.uuid}>
                            {mp.nome}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled>Nenhum marketplace de cardápio disponível</MenuItem>
                )}
            </Select>
        </FormControl>
    );
};

/**
 * Escolha da IA que assume a conversa depois das duas mensagens iniciais.
 *
 * "Nenhuma" é o padrão e mantém o comportamento de sempre: a conversa vira
 * alerta de pedido manual. Ao escolher ChatGPT, o prompt já nasce escrito a
 * partir do cadastro do cardápio e fica livre para edição.
 */
const ConfigurarIAAtendimento = ({ metadados, onChange }: {
    metadados: IComportamentoAtendimentoWhatsApp,
    onChange: (metadados: IComportamentoAtendimentoWhatsApp) => void
}) => {
    const ia = metadados.ia ?? TipoIAWhatsApp.NENHUMA;

    const handleTrocarIA = (novaIA: TipoIAWhatsApp) => {
        onChange({ ...metadados, ia: novaIA });
    };

    return (
        <>
            <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>IA do atendimento</InputLabel>
                <Select
                    label="IA do atendimento"
                    fullWidth
                    value={ia}
                    onChange={(e) => handleTrocarIA(e.target.value as TipoIAWhatsApp)}
                >
                    {Object.values(TipoIAWhatsApp).map((opcao) => (
                        <MenuItem key={opcao} value={opcao}>
                            {TipoIAWhatsAppDescricao[opcao]}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    Com "Nenhuma", a conversa vira um alerta para atendimento humano depois das mensagens iniciais.
                </FormHelperText>
            </FormControl>

            {ia === TipoIAWhatsApp.CHATGPT && (
                <Box
                    sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: "10px",
                        backgroundColor: theme => alpha(theme.palette.text.primary, 0.04),
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        A atendente é configurada automaticamente com o nome, o endereço, os horários
                        e o link do cardápio selecionado. Ela direciona o cliente para o pedido online
                        e marca a conversa para atendimento humano quando o assunto foge disso.
                    </Typography>
                </Box>
            )}
        </>
    );
};

const ContainerAtendimentoRestaurante = ({ comportamento, marketplaces }: { comportamento: IComportamentoWhatsApp, marketplaces: IMarketplace[] }) => {
    const navigate = useNavigate();
    const marketplace = marketplaces?.find(marketplace => marketplace.uuid == comportamento?.metadados?.marketplaceUuidCardapioDigital);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                p: 1.25,
                borderRadius: "10px",
                backgroundColor: theme => alpha(theme.palette.text.primary, 0.04)
            }}
        >
            <ArrowForward sx={{ fontSize: "1rem", color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
                Encaminha o cliente para pedir em:
            </Typography>
            {marketplace ? (
                <Typography
                    variant="caption"
                    color={TipoMarketplaceCor[marketplace?.tipo as TipoMarketplace]}
                    sx={{ cursor: "pointer", fontWeight: 600 }}
                    onClick={() => navigate(`/marketplace/${marketplace?.uuid}`)}
                >
                    {marketplace?.nome} ({TipoMarketplaceTitulo[marketplace.tipo]})
                </Typography>
            ) : (
                <Typography variant="caption" color={"error"} fontWeight={600}>
                    O marketplace foi excluído
                </Typography>
            )}
        </Box>
    );
};

const DialogEditarMensagens = ({
    comportamento,
    marketplaces,
    open,
    onClose,
    onSalvar,
    loading,
    estacoes,
}: {
    comportamento: IComportamentoWhatsApp;
    marketplaces: IMarketplace[];
    open: boolean;
    onClose: () => void;
    onSalvar: (mensagens: IMensagensCustomizadasComportamentoWhatsApp, metadados: IComportamentoAtendimentoWhatsApp) => void;
    loading: boolean;
    estacoes: IEstacaoKDS[];
}) => {
    const [mensagens, setMensagens] = useState<IMensagensCustomizadasComportamentoWhatsApp>(
        comportamento.mensagensCustomizadas ?? {}
    );
    const [metadados, setMetadados] = useState<IComportamentoAtendimentoWhatsApp>(
        comportamento.metadados ?? {}
    );

    const marketplaceCardapio = filtrarMarketplacesCardapio(marketplaces)
        .find(mp => mp.uuid === metadados.marketplaceUuidCardapioDigital);

    const handleChange = (campo: keyof IMensagensCustomizadasComportamentoWhatsApp, valor: string) => {
        setMensagens(prev => ({ ...prev, [campo]: valor || undefined }));
    };

    const handleChangeAtualizacao = (chave: string, valor: string) => {
        setMensagens(prev => ({
            ...prev,
            atualizacoes: {
                ...prev.atualizacoes,
                [chave]: valor || undefined,
            }
        }));
    };

    const adicionarEstacaoKDS = (uuid: string) => {
        const estacao = estacoes.find(s => s.uuid === uuid);
        if (!estacao) return;
        const jaExiste = (mensagens.atualizacoesKDS ?? []).some(a => a.estacaoKdsUuid === uuid);
        if (jaExiste) return;
        setMensagens(prev => ({
            ...prev,
            atualizacoesKDS: [
                ...(prev.atualizacoesKDS ?? []),
                { estacaoKdsUuid: uuid, nomeEstacao: estacao.nome, mensagem: '' }
            ]
        }));
    };

    const removerEstacaoKDS = (idx: number) => {
        setMensagens(prev => ({
            ...prev,
            atualizacoesKDS: (prev.atualizacoesKDS ?? []).filter((_, i) => i !== idx)
        }));
    };

    const atualizarMensagemEstacaoKDS = (idx: number, mensagem: string) => {
        setMensagens(prev => {
            const novas = [...(prev.atualizacoesKDS ?? [])];
            novas[idx] = { ...novas[idx], mensagem };
            return { ...prev, atualizacoesKDS: novas };
        });
    };

    const isSimples = comportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES;
    const isHibrida = comportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_HIBRIDA;
    const isIA = comportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA;
    const isAtualizacoes = comportamento.tipo === TipoComportamentoWhatsApp.ATUALIZACOES_PEDIDO;
    const isAtualizacoesKDS = comportamento.tipo === TipoComportamentoWhatsApp.ATUALIZACOES_KDS;
    const ehRespostaAutomatica = isSimples || isHibrida || isIA;
    const isResgate = comportamento.tipo === TipoComportamentoWhatsApp.RESGATE_PEDIDO_PERDIDO;
    const isAvaliacao = comportamento.tipo === TipoComportamentoWhatsApp.PEDIDO_AVALIACAO;
    const ehAgendado = isResgate || isAvaliacao;

    const atualizacoesLabels: { chave: keyof NonNullable<IMensagensCustomizadasComportamentoWhatsApp['atualizacoes']>, label: string }[] = [
        { chave: 'aguardando_confirmacao', label: 'Aguardando confirmação' },
        { chave: 'em_preparo', label: 'Em preparo' },
        { chave: 'em_transporte', label: 'Em transporte' },
        { chave: 'concluido', label: 'Concluído' },
        { chave: 'cancelado', label: 'Cancelado' },
        { chave: 'em_andamento', label: 'Em andamento' },
        { chave: 'pronto_para_retirada', label: 'Pronto para retirada' },
        { chave: 'em_negociacao', label: 'Em negociação' },
        { chave: 'aguardando_resposta_cliente', label: 'Aguardando resposta do cliente' },
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={{ '& .MuiDialog-paper': { maxHeight: '80vh' } }}>
            <DialogTitle>Personalizar mensagens — {TipoComportamentoWhatsAppDescricao[comportamento.tipo]}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Deixe o campo em branco para usar a mensagem padrão.
                </Typography>

                {ehRespostaAutomatica && (
                    <TextField
                        label="Aviso quando o cardápio estiver fechado"
                        fullWidth
                        multiline
                        minRows={4}
                        sx={{ mt: 1, mb: 1 }}
                        value={mensagens.mensagemLojaFechada ?? ''}
                        onChange={(e) => handleChange('mensagemLojaFechada', e.target.value)}
                        placeholder={montarMensagemLojaFechadaPadrao(marketplaceCardapio)}
                        helperText={'Enviado uma vez por conversa enquanto o cardápio estiver fechado, no lugar do atendimento. Variáveis: {nomeCliente}, {nomeCardapio}, {horarioFuncionamento}'}
                    />
                )}

                {isIA && (
                    <ConfigurarIAAtendimento
                        metadados={metadados}
                        onChange={setMetadados}
                    />
                )}

                {(isSimples) && (
                    <TextField
                        label="Mensagem de boas-vindas"
                        fullWidth
                        multiline
                        minRows={4}
                        sx={{ mt: 1 }}
                        value={mensagens.mensagemBoasVindas ?? ''}
                        onChange={(e) => handleChange('mensagemBoasVindas', e.target.value)}
                        placeholder={MENSAGENS_PADRAO.simples.mensagemBoasVindas}
                        helperText="Variáveis disponíveis: {nomeCliente}, {linkCardapio}"
                    />
                )}

                {isIA && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, fontStyle: 'italic' }}>
                            A 1ª mensagem (cumprimento com nome do cliente) é gerada automaticamente e não é customizável.
                        </Typography>
                        <TextField
                            label="2ª mensagem — link do cardápio"
                            fullWidth
                            multiline
                            minRows={4}
                            value={mensagens.mensagemLinkCardapio ?? ''}
                            onChange={(e) => handleChange('mensagemLinkCardapio', e.target.value)}
                            placeholder={MENSAGENS_PADRAO.ia.mensagemLinkCardapio}
                            helperText="Variáveis disponíveis: {nomeCardapio}, {linkCardapio}"
                        />
                    </>
                )}

                {isHibrida && (
                    <>
                        <TextField
                            label="1ª mensagem — pergunta de escolha"
                            fullWidth
                            multiline
                            minRows={4}
                            sx={{ mt: 1 }}
                            value={mensagens.mensagemEscolha ?? ''}
                            onChange={(e) => handleChange('mensagemEscolha', e.target.value)}
                            placeholder={MENSAGENS_PADRAO.hibrida.mensagemEscolha}
                            helperText="Variáveis disponíveis: {nomeCliente}, {cumprimento}"
                        />
                        <TextField
                            label="2ª mensagem — link do cardápio (ao escolher autoatendimento)"
                            fullWidth
                            multiline
                            minRows={3}
                            sx={{ mt: 2 }}
                            value={mensagens.mensagemAutoatendimento ?? ''}
                            onChange={(e) => handleChange('mensagemAutoatendimento', e.target.value)}
                            placeholder={MENSAGENS_PADRAO.hibrida.mensagemAutoatendimento}
                            helperText="Variáveis disponíveis: {nomeCardapio}, {linkCardapio}"
                        />
                    </>
                )}

                {ehAgendado && (
                    <>
                        <TextField
                            label={isResgate ? "Enviar depois de (minutos sem pedido)" : "Enviar depois de (minutos da conclusão)"}
                            type="number"
                            fullWidth
                            sx={{ mt: 1, mb: 2 }}
                            value={metadados.minutosAtraso ?? ''}
                            onChange={(e) => setMetadados(prev => ({
                                ...prev,
                                minutosAtraso: e.target.value ? Number(e.target.value) : undefined,
                            }))}
                            inputProps={{ min: 1 }}
                            placeholder="30"
                            helperText={isResgate
                                ? "Tempo de espera depois do envio do link do cardápio. Em branco usa 30 minutos."
                                : "Tempo de espera depois que o pedido é concluído. Em branco usa 30 minutos."}
                        />
                        <TextField
                            label={isResgate ? "Mensagem de resgate" : "Mensagem de avaliação"}
                            fullWidth
                            multiline
                            minRows={4}
                            value={(isResgate ? mensagens.mensagemResgatePedido : mensagens.mensagemAvaliacao) ?? ''}
                            onChange={(e) => handleChange(isResgate ? 'mensagemResgatePedido' : 'mensagemAvaliacao', e.target.value)}
                            placeholder={isResgate ? MENSAGENS_PADRAO.resgate.mensagemResgatePedido : MENSAGENS_PADRAO.avaliacao.mensagemAvaliacao}
                            helperText={isResgate
                                ? "Variáveis disponíveis: {nomeCliente}, {nomeCardapio}, {linkCardapio}"
                                : "Variáveis disponíveis: {nomeCliente}, {numeroPedido}, {linkAvaliacao}"}
                        />
                    </>
                )}

                {isAtualizacoes && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {atualizacoesLabels.map(({ chave, label }) => (
                            <TextField
                                key={chave}
                                label={label}
                                fullWidth
                                multiline
                                minRows={2}
                                value={mensagens.atualizacoes?.[chave] ?? ''}
                                onChange={(e) => handleChangeAtualizacao(chave, e.target.value)}
                                placeholder={MENSAGENS_PADRAO.atualizacoes[chave]}
                            />
                        ))}
                    </Box>
                )}

                {isAtualizacoesKDS && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Variáveis disponíveis: {'{nomeCliente}'}, {'{numeroPedido}'}, {'{nomeEstacao}'}
                        </Typography>

                        {(mensagens.atualizacoesKDS ?? []).map((entrada: IAtualizacaoKDSEstacao, idx: number) => (
                            <Box key={entrada.estacaoKdsUuid} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                        {entrada.nomeEstacao ?? entrada.estacaoKdsUuid}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Ex: Seu pedido está sendo preparado no {nomeEstacao}! 🍳"
                                        value={entrada.mensagem}
                                        onChange={(e) => atualizarMensagemEstacaoKDS(idx, e.target.value)}
                                    />
                                </Box>
                                <IconButton color="error" size="small" sx={{ mt: 3 }} onClick={() => removerEstacaoKDS(idx)}>
                                    <Delete />
                                </IconButton>
                            </Box>
                        ))}

                        {estacoes.filter(s => !(mensagens.atualizacoesKDS ?? []).some((a: IAtualizacaoKDSEstacao) => a.estacaoKdsUuid === s.uuid)).length > 0 && (
                            <FormControl size="small">
                                <InputLabel>Adicionar estação</InputLabel>
                                <Select
                                    label="Adicionar estação"
                                    value=""
                                    onChange={(e) => adicionarEstacaoKDS(e.target.value as string)}
                                >
                                    {estacoes
                                        .filter(s => !(mensagens.atualizacoesKDS ?? []).some((a: IAtualizacaoKDSEstacao) => a.estacaoKdsUuid === s.uuid))
                                        .map(s => (
                                            <MenuItem key={s.uuid} value={s.uuid}>{s.nome}</MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">Cancelar</Button>
                <Button
                    onClick={() => onSalvar(mensagens, metadados)}
                    variant={loading ? 'outlined' : 'contained'}
                >
                    {loading ? <CircularProgress size={20} /> : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const ConfigurarComportamentosWhatsApp = ({ marketplaces, marketplace, refresh }: ConfigurarComportamentosWhatsAppProps) => {
    const [open, setOpen] = useState(false);
    const [editandoIndex, setEditandoIndex] = useState<number | null>(null);
    const { salvar, loading } = useMarketplaces();
    const { estacoes } = useEstacoesKDS();
    const [novoComportamento, setNovoComportamento] = useState<IComportamentoWhatsApp>({
        tipo: TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES,
        metadados: {},
    });

    const integracao = marketplace?.configuracao?.integracao as IIntegracaoWhatsApp;

    const notificar = useSnackbar();

    const handleOpen = () => {
        const comportamentosExistentes = integracao?.comportamentos ?? [];

        const jaTemRespostaAutomatica = comportamentosExistentes.some((c) =>
            c.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA ||
            c.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES ||
            c.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_HIBRIDA
        );

        const jaTemAtualizacoesPedido = comportamentosExistentes.some(
            (c) => c.tipo === TipoComportamentoWhatsApp.ATUALIZACOES_PEDIDO
        );

        const jaTemAtualizacoesKDS = comportamentosExistentes.some(
            (c) => c.tipo === TipoComportamentoWhatsApp.ATUALIZACOES_KDS
        );

        const jaTemResgate = comportamentosExistentes.some(
            (c) => c.tipo === TipoComportamentoWhatsApp.RESGATE_PEDIDO_PERDIDO
        );

        const jaTemAvaliacao = comportamentosExistentes.some(
            (c) => c.tipo === TipoComportamentoWhatsApp.PEDIDO_AVALIACAO
        );

        let tipoDefault: TipoComportamentoWhatsApp | undefined;

        if (!jaTemRespostaAutomatica) {
            tipoDefault = TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES;
        } else if (!jaTemAtualizacoesPedido) {
            tipoDefault = TipoComportamentoWhatsApp.ATUALIZACOES_PEDIDO;
        } else if (!jaTemAtualizacoesKDS) {
            tipoDefault = TipoComportamentoWhatsApp.ATUALIZACOES_KDS;
        } else if (!jaTemResgate) {
            tipoDefault = TipoComportamentoWhatsApp.RESGATE_PEDIDO_PERDIDO;
        } else if (!jaTemAvaliacao) {
            tipoDefault = TipoComportamentoWhatsApp.PEDIDO_AVALIACAO;
        }

        if (!tipoDefault) {
            notificar("Você já vinculou todos os tipos de comportamento disponíveis.", { severity: "info" });
            return;
        }

        setNovoComportamento({
            tipo: tipoDefault,
            metadados: {},
        });
        setOpen(true);
    };
    const handleClose = () => setOpen(false);
    const handleChange = (field: keyof IComportamentoWhatsApp, value: any) => {
        setNovoComportamento(prev => ({ ...prev, [field]: value }));
    };

    const marketplacesCardapio = filtrarMarketplacesCardapio(marketplaces);

    const handleSelecionarMarketplaceCardapio = (uuid: string) => {
        handleChange("metadados", {
            ...(novoComportamento.metadados ?? {}),
            marketplaceUuidCardapioDigital: uuid,
        });
    };

    const handleExcluirComportamento = async (indexParaRemover: number) => {
        if (loading) return;

        const comportamentosAtuais = integracao.comportamentos ?? [];
        const comportamentosRestantes = comportamentosAtuais.filter((_, index) => index !== indexParaRemover);

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                integracao: {
                    ...integracao,
                    comportamentos: comportamentosRestantes
                }
            }
        });
        refresh();
    };

    const handleCadastrarComportamento = async () => {
        if (loading) return;

        if (!novoComportamento.tipo) {
            return notificar("Tipo de comportamento inválido", { severity: 'error' })
        }

        if (
            novoComportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA ||
            novoComportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES ||
            novoComportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_HIBRIDA
        ) {
            if (!novoComportamento.metadados?.marketplaceUuidCardapioDigital) {
                return notificar("Marketplace inválido", { severity: 'error' })
            }
        }

        const novosComportamentos = [];

        if (integracao.comportamentos?.length) {
            novosComportamentos.push(...integracao.comportamentos)
        }

        if (novoComportamento) {
            novosComportamentos.push(novoComportamento);
        }

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                integracao: {
                    ...integracao,
                    comportamentos: novosComportamentos
                }
            }
        });
        setNovoComportamento({
            tipo: TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES,
            metadados: {},
        })
        handleClose();
        refresh();
    };

    const handleSalvarMensagens = async (
        index: number,
        mensagens: IMensagensCustomizadasComportamentoWhatsApp,
        metadados: IComportamentoAtendimentoWhatsApp
    ) => {
        if (loading) return;

        const comportamentosAtuais = integracao.comportamentos ?? [];
        const comportamentosAtualizados = comportamentosAtuais.map((c, i) =>
            i === index ? { ...c, mensagensCustomizadas: mensagens, metadados } : c
        );

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                integracao: {
                    ...integracao,
                    comportamentos: comportamentosAtualizados
                }
            }
        });
        setEditandoIndex(null);
        refresh();
    };

    return (
        <SecaoConfiguracao
            titulo="Comportamentos"
            descricao="Automações que o WhatsApp executa no atendimento"
            icone={<SmartToyOutlined />}
            acoes={
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleOpen}
                >
                    Criar
                </Button>
            }
        >
            {integracao.comportamentos?.length ? (
                <ComportamentosContainer>
                    {integracao?.comportamentos?.map((comportamento, index) => {
                        const temMensagensCustomizadas = comportamento.mensagensCustomizadas &&
                            Object.keys(comportamento.mensagensCustomizadas).some(k => {
                                const val = (comportamento.mensagensCustomizadas as any)[k];
                                if (k === 'atualizacoes' && val) return Object.values(val).some(Boolean);
                                if (k === 'atualizacoesKDS' && Array.isArray(val)) return val.length > 0;
                                return Boolean(val);
                            });

                        return (
                            <ComportamentoCard key={index} variant="outlined">
                                <TituloComportamentoContainer>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                flexShrink: 0,
                                                borderRadius: "8px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.8125rem",
                                                fontWeight: 600,
                                                color: theme => theme.palette.primary.main,
                                                backgroundColor: theme => alpha(theme.palette.primary.main, 0.12)
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {TipoComportamentoWhatsAppDescricao[comportamento.tipo]}
                                        </Typography>
                                        {comportamento.metadados?.ia === TipoIAWhatsApp.CHATGPT && (
                                            <Chip
                                                icon={<AutoAwesome sx={{ fontSize: "0.9rem" }} />}
                                                label={TipoIAWhatsAppDescricao.CHATGPT}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                            />
                                        )}
                                        {temMensagensCustomizadas && (
                                            <Chip label="Personalizado" size="small" color="primary" variant="outlined" />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                        <Tooltip title="Personalizar mensagens">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    disabled={loading}
                                                    onClick={() => setEditandoIndex(index)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {loading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <IconButton size="small" color="error" onClick={() => handleExcluirComportamento(index)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TituloComportamentoContainer>
                                {(comportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA ||
                                    comportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES ||
                                    comportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_HIBRIDA) && (
                                    <ContainerAtendimentoRestaurante
                                        marketplaces={marketplaces}
                                        comportamento={comportamento}
                                    />
                                )}

                                {editandoIndex === index && (
                                    <DialogEditarMensagens
                                        comportamento={comportamento}
                                        marketplaces={marketplaces}
                                        open={true}
                                        onClose={() => setEditandoIndex(null)}
                                        onSalvar={(mensagens, metadados) => handleSalvarMensagens(index, mensagens, metadados)}
                                        estacoes={estacoes}
                                        loading={loading}
                                    />
                                )}
                            </ComportamentoCard>
                        );
                    })}
                </ComportamentosContainer>
            ) : (
                <NenhumComportamentoContainer>
                    <Typography variant="body2" fontWeight={500}>Nenhum comportamento cadastrado</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", mt: -0.5 }}>
                        Cadastre um comportamento para o WhatsApp responder e atualizar seus clientes automaticamente
                    </Typography>
                </NenhumComportamentoContainer>
            )}

            <Dialog open={open} onClose={handleClose} fullWidth>
                <DialogTitle>Novo Comportamento</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: "1.5rem" }}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                            label="Tipo"
                            fullWidth
                            value={novoComportamento.tipo}
                            onChange={(e) => setNovoComportamento({
                                tipo: e.target.value as TipoComportamentoWhatsApp,
                                metadados: {},
                            })}
                        >
                            {(() => {
                                const comportamentosExistentes = integracao?.comportamentos ?? [];

                                const jaTemRespostaAutomatica = comportamentosExistentes.some((c) =>
                                    c.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA ||
                                    c.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES ||
                                    c.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_HIBRIDA
                                );

                                const jaTemAtualizacoesPedido = comportamentosExistentes.some(
                                    (c) => c.tipo === TipoComportamentoWhatsApp.ATUALIZACOES_PEDIDO
                                );

                                const jaTemAtualizacoesKDS = comportamentosExistentes.some(
                                    (c) => c.tipo === TipoComportamentoWhatsApp.ATUALIZACOES_KDS
                                );

                                const jaTemResgate = comportamentosExistentes.some(
                                    (c) => c.tipo === TipoComportamentoWhatsApp.RESGATE_PEDIDO_PERDIDO
                                );

                                const jaTemAvaliacao = comportamentosExistentes.some(
                                    (c) => c.tipo === TipoComportamentoWhatsApp.PEDIDO_AVALIACAO
                                );

                                const tiposDisponiveis = Object.values(TipoComportamentoWhatsApp).filter((tipo) => {
                                    if (
                                        tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA ||
                                        tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES ||
                                        tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_HIBRIDA
                                    ) {
                                        return !jaTemRespostaAutomatica;
                                    }

                                    if (tipo === TipoComportamentoWhatsApp.ATUALIZACOES_PEDIDO) {
                                        return !jaTemAtualizacoesPedido;
                                    }

                                    if (tipo === TipoComportamentoWhatsApp.ATUALIZACOES_KDS) {
                                        return !jaTemAtualizacoesKDS;
                                    }

                                    if (tipo === TipoComportamentoWhatsApp.RESGATE_PEDIDO_PERDIDO) {
                                        return !jaTemResgate;
                                    }

                                    if (tipo === TipoComportamentoWhatsApp.PEDIDO_AVALIACAO) {
                                        return !jaTemAvaliacao;
                                    }

                                    return true;
                                });

                                return tiposDisponiveis.map((tipo) => (
                                    <MenuItem key={tipo as string} value={tipo as string}>
                                        {TipoComportamentoWhatsAppDescricao[tipo as keyof typeof TipoComportamentoWhatsAppDescricao]}
                                    </MenuItem>
                                ));
                            })()}
                        </Select>
                    </FormControl>
                    {(novoComportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA ||
                        novoComportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_SIMPLES ||
                        novoComportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_HIBRIDA) && (
                        <>
                            <SelecionarMarketplaceCardapio
                                marketplaces={marketplaces}
                                valor={novoComportamento.metadados?.marketplaceUuidCardapioDigital}
                                onChange={handleSelecionarMarketplaceCardapio}
                            />
                            {novoComportamento.tipo === TipoComportamentoWhatsApp.RESPOSTA_AUTOMATICA_IA && (
                                <ConfigurarIAAtendimento
                                    metadados={novoComportamento.metadados ?? {}}
                                    onChange={(metadados) => handleChange("metadados", metadados)}
                                />
                            )}
                        </>
                    )}
                    {(novoComportamento.tipo === TipoComportamentoWhatsApp.RESGATE_PEDIDO_PERDIDO ||
                        novoComportamento.tipo === TipoComportamentoWhatsApp.PEDIDO_AVALIACAO) && (
                        <TextField
                            label={novoComportamento.tipo === TipoComportamentoWhatsApp.RESGATE_PEDIDO_PERDIDO
                                ? "Enviar depois de (minutos sem pedido)"
                                : "Enviar depois de (minutos da conclusão)"}
                            type="number"
                            fullWidth
                            sx={{ mt: "1.5rem" }}
                            value={novoComportamento.metadados?.minutosAtraso ?? ''}
                            onChange={(e) => handleChange("metadados", {
                                ...novoComportamento.metadados,
                                minutosAtraso: e.target.value ? Number(e.target.value) : undefined,
                            })}
                            inputProps={{ min: 1 }}
                            placeholder="30"
                            helperText="Em branco usa 30 minutos. A mensagem pode ser personalizada depois de cadastrar."
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">Cancelar</Button>
                    <Button
                        onClick={handleCadastrarComportamento}
                        variant={loading ? 'outlined' : "contained"}
                    >
                        {
                            loading
                                ? <CircularProgress size={20} />
                                : 'Cadastrar'
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </SecaoConfiguracao>
    );
}
